import { onCleanup } from 'solid-js'
import { resetZoom, zoomBy, zoomToFit, zoomToSelection } from './canvas/cameraActions.ts'
import { activeTool, setSpaceDown, toggleGrid, useTool } from './editor.ts'
import {
  bringForward,
  bringToFront,
  clearSelection,
  deleteNodes,
  duplicateSelection,
  groupSelection,
  nudgeSelection,
  redo,
  selectAll,
  selection,
  sendBackward,
  sendToBack,
  undo,
  ungroupSelection,
} from './scene/store.ts'
import { copySelection } from './clipboard.ts'
import { TOOL_KEYS } from './tools/index.ts'
import { isTypingTarget } from './util/dom.ts'

const NUDGE = 1
const NUDGE_BIG = 10

export function installShortcuts(): void {
  const onKeyDown = (e: KeyboardEvent) => {
    if (isTypingTarget(e.target)) return

    const mod = e.ctrlKey || e.metaKey
    const ids = selection()

    if (e.code === 'Space' && !mod) {
      setSpaceDown(true)
      e.preventDefault()
      return
    }

    if (mod) {
      switch (e.key.toLowerCase()) {
        case 'z':
          e.preventDefault()
          if (e.shiftKey) redo()
          else undo()
          return
        case 'y':
          e.preventDefault()
          redo()
          return
        case 'a':
          e.preventDefault()
          selectAll()
          return
        case 'g':
          e.preventDefault()
          if (e.shiftKey) ungroupSelection()
          else groupSelection()
          return
        case 'c':
          if (copySelection()) e.preventDefault()
          return
        case 'd':
          e.preventDefault()
          duplicateSelection()
          return
        case ']':
          e.preventDefault()
          bringToFront(ids)
          return
        case '[':
          e.preventDefault()
          sendToBack(ids)
          return
        case '0':
          e.preventDefault()
          resetZoom()
          return
        case "'":
          e.preventDefault()
          toggleGrid()
          return
        case '1':
          e.preventDefault()
          zoomToFit()
          return
        case '2':
          e.preventDefault()
          zoomToSelection()
          return
        case '=':
        case '+':
          e.preventDefault()
          zoomBy(1.2)
          return
        case '-':
          e.preventDefault()
          zoomBy(1 / 1.2)
          return
      }
      return
    }

    switch (e.key) {
      case 'Delete':
      case 'Backspace':
        if (ids.length > 0) {
          e.preventDefault()
          deleteNodes(ids)
        }
        return
      case 'Escape':
        if (activeTool() !== 'select') useTool('select')
        else clearSelection()
        return
      case ']':
        bringForward(ids)
        return
      case '[':
        sendBackward(ids)
        return
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'ArrowUp':
      case 'ArrowDown': {
        if (ids.length === 0) return
        e.preventDefault()
        const step = e.shiftKey ? NUDGE_BIG : NUDGE
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0
        nudgeSelection(dx, dy)
        return
      }
    }

    const tool = TOOL_KEYS[e.key.toLowerCase()]
    if (tool) useTool(tool)
  }

  const onKeyUp = (e: KeyboardEvent) => {
    if (e.code === 'Space') setSpaceDown(false)
  }

  // Alt-tabbing away mid-pan would otherwise leave space stuck down.
  const onBlur = () => setSpaceDown(false)

  window.addEventListener('keydown', onKeyDown)
  window.addEventListener('keyup', onKeyUp)
  window.addEventListener('blur', onBlur)

  onCleanup(() => {
    window.removeEventListener('keydown', onKeyDown)
    window.removeEventListener('keyup', onKeyUp)
    window.removeEventListener('blur', onBlur)
  })
}
